import { useCallback, useEffect, useState } from "react";
import { useBlocker } from "react-router-dom";

export interface UseUnsavedChangesGuardOptions {
  /** Whether to enable navigation blocking */
  enabled?: boolean;
  /** Custom message for the browser's beforeunload dialog */
  message?: string;
}

export interface UseUnsavedChangesGuardReturn {
  /** Whether there's a pending navigation confirmation */
  showConfirmation: boolean;
  /** Confirm and proceed with navigation */
  confirm: () => void;
  /** Cancel navigation and stay on current page */
  cancel: () => void;
}

/**
 * Hook to guard navigation when there are unsaved changes.
 *
 * @param hasUnsavedChanges - Boolean indicating if there are unsaved changes
 * @param options - Configuration options
 * @returns Navigation guard controls
 *
 * @example
 * ```tsx
 * const { form, save, discard } = useBusinessSettings()
 * const navigationGuard = useUnsavedChangesGuard(form.formState.isDirty)
 *
 * // In your JSX:
 * <ConfirmationDialog
 *   open={navigationGuard.showConfirmation}
 *   onConfirm={navigationGuard.confirm}
 *   onCancel={navigationGuard.cancel}
 * />
 * ```
 */
export function useUnsavedChangesGuard(
  hasUnsavedChanges: boolean,
  options: UseUnsavedChangesGuardOptions = {}
): UseUnsavedChangesGuardReturn {
  const { enabled = true, message = "You have unsaved changes. Are you sure you want to leave?" } =
    options;

  const [showConfirmation, setShowConfirmation] = useState(false);

  // Check if we should block navigation
  const shouldBlock = enabled && hasUnsavedChanges;

  // React Router v6 blocker
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block if navigating to the same location
    if (currentLocation.pathname === nextLocation.pathname) {
      return false;
    }
    return shouldBlock;
  });

  // Show confirmation dialog when navigation is blocked
  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowConfirmation(true);
    }
  }, [blocker.state]);

  // Browser beforeunload event handler
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldBlock) {
        event.preventDefault();
        // Modern browsers ignore the return value and show their own message
        event.returnValue = message;
        return message;
      }
    };

    if (enabled) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [enabled, shouldBlock, message]);

  const confirm = useCallback(() => {
    setShowConfirmation(false);
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  }, [blocker]);

  const cancel = useCallback(() => {
    setShowConfirmation(false);
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  }, [blocker]);

  return {
    showConfirmation,
    confirm,
    cancel,
  };
}
